import { useState } from 'react'
import { X, Check, AlertCircle } from 'lucide-react'
import axios from 'axios'

export function ChangePasswordModal({ isOpen, onClose }) {
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const [messageType, setMessageType] = useState('success')
  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  })

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleChangePassword = async () => {
    // Validation
    if (!formData.current_password) {
      setMessageType('error')
      setMessage('Please enter your current password')
      return
    }
    if (!formData.new_password) {
      setMessageType('error')
      setMessage('Please enter a new password')
      return
    }
    if (!formData.confirm_password) {
      setMessageType('error')
      setMessage('Please confirm your new password')
      return
    }
    if (formData.new_password !== formData.confirm_password) {
      setMessageType('error')
      setMessage('New passwords do not match')
      return
    }
    if (formData.new_password.length < 8) {
      setMessageType('error')
      setMessage('Password must be at least 8 characters')
      return
    }

    setLoading(true)
    setMessage(null)

    try {
      const API_URL = import.meta.env.VITE_API_URL || 'https://wqlive.ezxdemo.com'
      const API_BASE_PATH = import.meta.env.VITE_API_BASE_PATH || '/api'
      const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
      const token = localStorage.getItem(JWT_STORAGE_KEY)

      if (!token) {
        throw new Error('Authentication token not found. Please login again.')
      }

      const response = await axios.post(
        `${API_URL}${API_BASE_PATH}/account/change-password/`,
        {
          current_password: formData.current_password,
          new_password: formData.new_password
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      setMessageType('success')
      setMessage('Password changed successfully!')
      
      // Reset form
      setFormData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      })
      
      setTimeout(() => {
        onClose()
      }, 2000)
    } catch (error) {
      setMessageType('error')
      let errorMessage = 'Failed to change password. Please try again.'
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }
      setMessage(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-start justify-between p-8 border-b border-gray-100 sticky top-0 bg-white">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900">Change Password</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition ml-4"
            title="Close"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Message */}
          {message && (
            <div className={`mb-6 p-4 rounded-lg text-sm font-medium flex items-center gap-2 ${
              messageType === 'success' 
                ? 'bg-green-50 border border-green-200 text-green-800' 
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}>
              {messageType === 'success' ? (
                <Check className="w-4 h-4 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
              )}
              {message}
            </div>
          )}

          <div className="space-y-4">
            {/* Current Password */}
            <input
              type="password"
              name="current_password"
              value={formData.current_password}
              onChange={handleInputChange}
              placeholder="Current Password"
              disabled={loading}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50"
            />

            {/* New Password */}
            <input
              type="password"
              name="new_password"
              value={formData.new_password}
              onChange={handleInputChange}
              placeholder="New Password"
              disabled={loading}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50"
            />

            {/* Confirm New Password */}
            <input
              type="password"
              name="confirm_password"
              value={formData.confirm_password}
              onChange={handleInputChange}
              placeholder="Confirm New Password"
              disabled={loading}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 transition disabled:bg-gray-100 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleChangePassword}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition disabled:bg-blue-400 font-semibold"
          >
            {loading ? 'Changing...' : 'CHANGE PASSWORD'}
          </button>
        </div>
      </div>
    </div>
  )
}
